// WebSocket client for multiplayer game communication

import {
  ClientMessage,
  ServerMessage,
  PlayerRole,
  WizardType,
  SpellType,
  CheerType,
  EmoteType,
  ChallengeMode,
  RoomState,
  GameState,
  PlayerStats,
  MatchResult,
  SeasonInfo,
  SeasonalPlayerStats,
  ChallengeModeConfig,
  BetPoolState,
  BetResult,
  DustTransactionType,
  ShopItem,
  validateServerMessage,
} from './protocol';

type EventHandler<T> = (data: T) => void;

interface MultiplayerEvents {
  joined: { role: PlayerRole; roomState: RoomState };
  playerJoined: { role: PlayerRole; name?: string; roomState: RoomState };
  wizardSelected: { player: PlayerRole; wizardType: WizardType };
  playerReady: { player: PlayerRole };
  gameStart: { gameState: GameState };
  spellCast: { caster: PlayerRole; spell: SpellType; timestamp: number };
  damage: { target: PlayerRole; amount: number; newHealth: number; shieldAbsorbed: number };
  slowApplied: { target: PlayerRole; duration: number; multiplier: number };
  positionUpdate: { player: PlayerRole; x: number; z: number };
  gameOver: { winner: PlayerRole; matchResult?: MatchResult };
  cheer: { from: string; cheerType: CheerType; timestamp: number };
  spectatorCount: { count: number };
  playerDisconnected: { player: PlayerRole };
  error: { message: string };
  connected: void;
  disconnected: void;
  playerStats: { stats: PlayerStats };
  matchStats: { player1Spells: number; player2Spells: number; duration: number };
  spectatorChat: { from: string; message: string; timestamp: number };
  rateLimited: { cooldownRemaining: number };
  emote: { from: string; emoteType: EmoteType; player: PlayerRole; timestamp: number };
  seasonInfo: { season: SeasonInfo; playerSeasonStats?: SeasonalPlayerStats };
  challengeModeSet: { mode: ChallengeMode | null; config?: ChallengeModeConfig };
  dustBalance: { dust: number };
  betPlaced: { pool: BetPoolState };
  betLocked: { pool: BetPoolState };
  betResult: { result: BetResult };
  dustEarned: { amount: number; reason: DustTransactionType; newBalance: number };
  purchaseResult: { success: boolean; cosmeticId: string; newBalance: number; error?: string };
  shopItems: { items: ShopItem[] };
}

// Generate or retrieve a persistent player ID
function getPlayerId(): string {
  const key = 'wizard-duel-player-id';
  let id = localStorage.getItem(key);
  if (!id) {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    id = 'p_';
    for (let i = 0; i < 12; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    localStorage.setItem(key, id);
  }
  return id;
}

export class MultiplayerClient {
  private ws: WebSocket | null = null;
  private roomId: string = '';
  private role: PlayerRole | null = null;
  private playerId: string;
  private handlers: Map<keyof MultiplayerEvents, Set<EventHandler<unknown>>> = new Map();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private readonly maxReconnectDelay: number = 30000; // Cap at 30 seconds

  constructor() {
    this.playerId = getPlayerId();

    // Initialize handler sets
    const events: (keyof MultiplayerEvents)[] = [
      'joined', 'playerJoined', 'wizardSelected', 'playerReady',
      'gameStart', 'spellCast', 'damage', 'slowApplied', 'positionUpdate',
      'gameOver', 'cheer', 'spectatorCount', 'playerDisconnected', 'error',
      'connected', 'disconnected', 'playerStats', 'matchStats',
      'spectatorChat', 'rateLimited', 'emote',
      'seasonInfo', 'challengeModeSet',
      'dustBalance', 'betPlaced', 'betLocked', 'betResult', 'dustEarned', 'purchaseResult', 'shopItems',
    ];
    events.forEach(event => this.handlers.set(event, new Set()));
  }

  connect(roomId: string, playerName?: string): void {
    this.roomId = roomId;

    // Determine WebSocket URL based on environment
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // In development, worker runs on different port
    // In production, use the dedicated worker subdomain
    const isDev = host.includes('localhost') || host.includes('127.0.0.1');
    const workerHost = isDev ? 'localhost:8788' : 'wizard-duel-api.neenhouse.workers.dev';

    const wsUrl = `${protocol}//${workerHost}/ws/room/${roomId}`;

    console.log('Multiplayer: Connecting to', wsUrl);
    if (isDev) {
      console.log('Multiplayer: Running in development mode - make sure worker is running with: cd worker && wrangler dev');
    }

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('Multiplayer: WebSocket connected');
      this.reconnectAttempts = 0;
      this.emit('connected', undefined);

      // Send join message with persistent playerId
      console.log('Multiplayer: Sending join message');
      this.send({ type: 'join', name: playerName, playerId: this.playerId });
    };

    this.ws.onmessage = (event) => {
      try {
        const rawData = JSON.parse(event.data);
        const message = validateServerMessage(rawData);

        if (message) {
          console.log('Multiplayer: Received message', message.type);
          this.handleMessage(message);
        } else {
          console.warn('Multiplayer: Invalid message received, ignoring');
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    this.ws.onclose = (event) => {
      console.log('Multiplayer: WebSocket closed', event.code, event.reason);
      this.emit('disconnected', undefined);
      this.attemptReconnect(playerName);
    };

    this.ws.onerror = (error) => {
      console.error('Multiplayer: WebSocket error:', error);
      if (isDev) {
        console.error('Multiplayer: Is the worker running? Start it with: cd worker && wrangler dev');
      }
    };
  }

  private attemptReconnect(playerName?: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      // Emit error event for UI to display
      this.emit('error', { message: 'Connection lost. Please refresh the page to reconnect.' });
      return;
    }

    this.reconnectAttempts++;

    // Exponential backoff with jitter, capped at maxReconnectDelay
    const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    const jitter = Math.random() * 1000; // Add 0-1 second of jitter
    const delay = Math.min(baseDelay + jitter, this.maxReconnectDelay);

    console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (this.roomId) {
        this.connect(this.roomId, playerName);
      }
    }, delay);
  }

  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'joined':
        this.role = message.role;
        this.emit('joined', { role: message.role, roomState: message.roomState });
        break;
      case 'player_joined':
        this.emit('playerJoined', { role: message.role, name: message.name, roomState: message.roomState });
        break;
      case 'wizard_selected':
        this.emit('wizardSelected', { player: message.player, wizardType: message.wizardType });
        break;
      case 'player_ready':
        this.emit('playerReady', { player: message.player });
        break;
      case 'game_start':
        this.emit('gameStart', { gameState: message.gameState });
        break;
      case 'spell_cast':
        this.emit('spellCast', { caster: message.caster, spell: message.spell, timestamp: message.timestamp });
        break;
      case 'damage':
        this.emit('damage', {
          target: message.target,
          amount: message.amount,
          newHealth: message.newHealth,
          shieldAbsorbed: message.shieldAbsorbed,
        });
        break;
      case 'slow_applied':
        this.emit('slowApplied', {
          target: message.target,
          duration: message.duration,
          multiplier: message.multiplier,
        });
        break;
      case 'position_update':
        this.emit('positionUpdate', {
          player: message.player,
          x: message.x,
          z: message.z,
        });
        break;
      case 'game_over':
        this.emit('gameOver', { winner: message.winner, matchResult: message.matchResult });
        break;
      case 'cheer':
        this.emit('cheer', { from: message.from, cheerType: message.cheerType, timestamp: message.timestamp });
        break;
      case 'spectator_count':
        this.emit('spectatorCount', { count: message.count });
        break;
      case 'player_disconnected':
        this.emit('playerDisconnected', { player: message.player });
        break;
      case 'error':
        this.emit('error', { message: message.message });
        break;
      case 'player_stats':
        this.emit('playerStats', { stats: message.stats });
        break;
      case 'match_stats':
        this.emit('matchStats', {
          player1Spells: message.player1Spells,
          player2Spells: message.player2Spells,
          duration: message.duration,
        });
        break;
      case 'spectator_chat':
        this.emit('spectatorChat', {
          from: message.from,
          message: message.message,
          timestamp: message.timestamp,
        });
        break;
      case 'rate_limited':
        this.emit('rateLimited', { cooldownRemaining: message.cooldownRemaining });
        break;
      case 'emote':
        this.emit('emote', {
          from: message.from,
          emoteType: message.emoteType,
          player: message.player,
          timestamp: message.timestamp,
        });
        break;
      case 'season_info':
        this.emit('seasonInfo', {
          season: message.season,
          playerSeasonStats: message.playerSeasonStats,
        });
        break;
      case 'challenge_mode_set':
        this.emit('challengeModeSet', {
          mode: message.mode,
          config: message.config,
        });
        break;
      case 'dust_balance':
        this.emit('dustBalance', { dust: message.dust });
        break;
      case 'bet_placed':
        this.emit('betPlaced', { pool: message.pool });
        break;
      case 'bet_locked':
        this.emit('betLocked', { pool: message.pool });
        break;
      case 'bet_result':
        this.emit('betResult', { result: message.result });
        break;
      case 'dust_earned':
        this.emit('dustEarned', {
          amount: message.amount,
          reason: message.reason,
          newBalance: message.newBalance,
        });
        break;
      case 'purchase_result':
        this.emit('purchaseResult', {
          success: message.success,
          cosmeticId: message.cosmeticId,
          newBalance: message.newBalance,
          error: message.error,
        });
        break;
      case 'shop_items':
        this.emit('shopItems', { items: message.items });
        break;
    }
  }

  private send(message: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  selectWizard(wizardType: WizardType): void {
    this.send({ type: 'wizard_select', wizardType });
  }

  setReady(): void {
    this.send({ type: 'ready' });
  }

  castSpell(spell: SpellType): void {
    this.send({ type: 'cast_spell', spell });
  }

  sendPosition(x: number, z: number): void {
    this.send({ type: 'position_update', x, z });
  }

  cheer(cheerType: CheerType): void {
    this.send({ type: 'cheer', cheerType });
  }

  requestStats(): void {
    this.send({ type: 'request_stats' });
  }

  sendEmote(emoteType: EmoteType): void {
    this.send({ type: 'emote', emoteType });
  }

  requestSeasonInfo(): void {
    this.send({ type: 'request_season_info' });
  }

  setChallengeMode(mode: ChallengeMode | null): void {
    this.send({ type: 'set_challenge_mode', mode });
  }

  sendSpectatorChat(message: string): void {
    this.send({ type: 'spectator_chat', message });
  }

  placeBet(amount: number, backedPlayer: PlayerRole): void {
    this.send({ type: 'place_bet', amount, backedPlayer });
  }

  requestDustBalance(): void {
    this.send({ type: 'request_dust_balance' });
  }

  purchaseCosmetic(cosmeticId: string): void {
    this.send({ type: 'purchase_cosmetic', cosmeticId });
  }

  disconnect(): void {
    this.maxReconnectAttempts = 0; // Prevent reconnection
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  getRole(): PlayerRole | null {
    return this.role;
  }

  getPlayerId(): string {
    return this.playerId;
  }

  getRoomId(): string {
    return this.roomId;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  isSpectator(): boolean {
    return this.role === 'spectator';
  }

  isPlayer(): boolean {
    return this.role === 'player1' || this.role === 'player2';
  }

  // Event handling
  on<K extends keyof MultiplayerEvents>(
    event: K,
    handler: EventHandler<MultiplayerEvents[K]>
  ): () => void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.add(handler as EventHandler<unknown>);
    }
    return () => this.off(event, handler);
  }

  off<K extends keyof MultiplayerEvents>(
    event: K,
    handler: EventHandler<MultiplayerEvents[K]>
  ): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler<unknown>);
    }
  }

  private emit<K extends keyof MultiplayerEvents>(
    event: K,
    data: MultiplayerEvents[K]
  ): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }
}
